import { checkPattern, MAX_REGEX, MAX_REPETITIONS } from './elementFormat/pattern';

/**
 * A short text's custom pattern as PIECES (KPR-956, KPE-010; the owner's
 * rulings p01 and p02, 2026-10-18): rows a person picks — "Starts with 3
 * capital letters, then the text “-”, then 4 digits" — over the regex string
 * the element stores. Nothing here changes what is stored: the stored option is
 * still `{ regex, flags?, message }`, held by the vendored
 * utils/elementFormat/pattern.js (byte-identical with keepr-api) and the API's
 * timed probe. This module only writes that string from rows, reads it back
 * into rows when it can, and proposes rows from examples.
 *
 *   rowsToRegex(pieces)       the regex the rows write
 *   parseRegex(regex)         the rows a stored regex reads as, or null when it
 *                             has a part the rows cannot show
 *   fitsPieces(regex, flags)  p02: does a stored regex reopen as rows? Only
 *                             when the rows write it back BYTE-IDENTICAL and
 *                             have no problem
 *   piecesProblem(pieces, f)  why the rows cannot be written yet, or null
 *   proposeFromExamples(...)  p01: rows that fit every example, and the one
 *                             line saying how keepr read them
 *   describeRows(rows)        the rows as a sentence
 *
 * Byte-identity. A row read from a stored regex keeps its own spelling (`src`)
 * and writes it until the person edits that row, so `[a-zA-Z]`, `\d` and a
 * leading `^` / trailing `$` come back exactly as they were. A row the person
 * builds writes the canonical spelling (CANONICAL below).
 *
 * Cost. The API refuses a custom regex that is slow on a long value (its probe,
 * 5 ms on values of 255 characters) and gives every evaluation 20 ms. A flat
 * sequence of rows is slow only where neighbouring rows can take the same
 * characters AND more than one of them takes a varying count: the engine then
 * tries every way of sharing the characters out. `piecesProblem` measures that
 * (chainCost) and refuses rows past MAX_COST, so the rows never write a pattern
 * the probe or the budget could refuse (pinned by patternPieces.test.js).
 */

// The longest value a short text holds: no count needs to go past it.
export const MAX_COUNT = 255;
// The most rows a pattern may have. Each counted row is one of the vendored
// rule's 25 repeat marks; exactly-one rows carry none.
export const MAX_ROWS = MAX_REPETITIONS;
// The most ways a pattern's rows may share a value's characters out (see
// chainCost). Two neighbouring "one or more" rows of the same kind are 256 x
// 256, and checking a 255-character value against them takes about 0.2 ms; a
// third varying row beside them is refused. Measured against keepr-api's own
// probe and budget (KPR-956): at 2^20 the slowest shapes took 2 to 22 ms, past
// the probe's 5 ms, so the bound sits at 2^16, a wide margin under it.
export const MAX_COST = 1 << 16;

// The kinds a row takes. `atom` is the canonical spelling; `noun` and `nouns`
// read it in a sentence. `cats` are the character categories it takes, for the
// cost rule (u upper, l lower, d digit, s space, * everything).
export const KINDS = Object.freeze([
	{ id: 'letters', label: 'Letters', atom: '[A-Za-z]', noun: 'letter', nouns: 'letters', cats: 'ul' },
	{ id: 'capitals', label: 'Capital letters', atom: '[A-Z]', noun: 'capital letter', nouns: 'capital letters', cats: 'u' },
	{ id: 'small', label: 'Small letters', atom: '[a-z]', noun: 'small letter', nouns: 'small letters', cats: 'l' },
	{ id: 'digits', label: 'Digits', atom: '[0-9]', noun: 'digit', nouns: 'digits', cats: 'd' },
	{ id: 'alnum', label: 'Letters or digits', atom: '[A-Za-z0-9]', noun: 'letter or digit', nouns: 'letters or digits', cats: 'uld' },
	{ id: 'capitals-digits', label: 'Capital letters or digits', atom: '[A-Z0-9]', noun: 'capital letter or digit', nouns: 'capital letters or digits', cats: 'ud' },
	{ id: 'small-digits', label: 'Small letters or digits', atom: '[a-z0-9]', noun: 'small letter or digit', nouns: 'small letters or digits', cats: 'ld' },
	{ id: 'space', label: 'A space', atom: ' ', noun: 'space', nouns: 'spaces', cats: 's' },
	{ id: 'text', label: 'The text…', needsText: true },
	{ id: 'chars', label: 'Any of these characters…', needsText: true },
	{ id: 'anything', label: 'Anything', atom: '.', noun: 'character of any kind', nouns: 'characters of any kind', cats: '*' },
]);

const KIND_BY_ID = Object.fromEntries(KINDS.map(k => [k.id, k]));
export const kindOf = id => KIND_BY_ID[id] || null;

// How many of the kind a row takes.
export const COUNT_TYPES = Object.freeze([
	{ value: 'exactly', label: 'Exactly' },
	{ value: 'range', label: 'From … to …' },
	{ value: 'some', label: 'One or more' },
	{ value: 'any', label: 'Any number' },
]);

// The ranges a character class may be built of, and which kind each set of
// them is (in any order: `[a-zA-Z]` is letters as much as `[A-Za-z]`).
const RANGE_KINDS = {
	'A-Z|a-z': 'letters',
	'A-Z': 'capitals',
	'a-z': 'small',
	'0-9': 'digits',
	'0-9|A-Z|a-z': 'alnum',
	'0-9|A-Z': 'capitals-digits',
	'0-9|a-z': 'small-digits',
};
const RANGES = ['A-Z', 'a-z', '0-9'];

let nextId = 0;
const newId = () => `p${++nextId}`;

/** A fresh row: one letter. */
export function newRow(partial = {}) {
	return { id: newId(), kind: 'letters', text: '', count: { type: 'exactly', n: 1, m: null }, ...partial };
}

/** The same row with a change — its stored spelling dropped, so it writes the canonical one. */
export function editRow(row, change) {
	const next = { ...row, ...change };
	delete next.src;
	return next;
}

export const EMPTY_PIECES = Object.freeze({ rows: [], lead: '', trail: '' });

// ------------------------------------------------------------------ writing

// Outside a class these stand for something else and are written with a
// backslash; inside one, these.
const TEXT_SPECIAL = /[\\^$.|?*+()[\]{}]/g;
const CLASS_SPECIAL = /[\\\]^[-]/g;

const escapeText = text => String(text).replace(TEXT_SPECIAL, '\\$&');
const escapeClass = chars => String(chars).replace(CLASS_SPECIAL, '\\$&');

function quantifier(count) {
	switch (count?.type) {
		case 'exactly': return count.n === 1 ? '' : `{${count.n}}`;
		case 'range': return count.n === 0 && count.m === 1 ? '?' : `{${count.n},${count.m}}`;
		case 'some': return '+';
		case 'any': return '*';
		default: return '';
	}
}

/** One row's canonical spelling. */
export function canonicalRow(row) {
	const q = quantifier(row.count);
	if (row.kind === 'text') {
		const text = String(row.text || '');
		if (!q) return escapeText(text);
		// One UTF-16 unit takes the count on its own; anything longer (an emoji
		// is two) is grouped so the count applies to all of it.
		return text.length === 1 ? escapeText(text) + q : `(?:${escapeText(text)})${q}`;
	}
	if (row.kind === 'chars') return `[${escapeClass(row.text || '')}]${q}`;
	const kind = kindOf(row.kind);
	return kind ? kind.atom + q : '';
}

const rowRegex = row => (typeof row.src === 'string' ? row.src : canonicalRow(row));

/** The regex the rows write: each row's own spelling, between the kept `^` and `$`. */
export function rowsToRegex(pieces) {
	const rows = pieces?.rows || [];
	return (pieces?.lead || '') + rows.map(rowRegex).join('') + (pieces?.trail || '');
}

/**
 * The regex of the rows that are complete, for "Edit as expression" while a row
 * is still being filled in: the person then sees what the rest writes.
 */
export function completeRowsRegex(pieces) {
	const rows = (pieces?.rows || []).filter(row => !rowProblem(row));
	return rowsToRegex({ ...pieces, rows });
}

// ------------------------------------------------------------------ reading

const isSurrogate = c => {
	const code = c.charCodeAt(0);
	return code >= 0xd800 && code <= 0xdfff;
};
// A character a backslash may make literal: ASCII punctuation, and the space.
const IDENTITY_ESCAPE = /^[ !-/:-@[-`{-~]$/;

/**
 * parseRegex(regex) -> { rows, lead, trail } | null
 *
 * The rows a stored regex reads as. The grammar is flat: a sequence of
 * character classes of the kinds above, `\d`, `.`, a space, literal and
 * escaped characters, `(?:text)` or `(text)` of literal characters, each with
 * an optional `*`, `+`, `?`, `{n}`, `{n,m}`, `{0,}` or `{1,}` — and a leading
 * `^` and trailing `$`, which an implicitly anchored pattern does not need but
 * people write. Alternation, other groups, negated classes, other escapes,
 * lazy counts and counts past 255 read as null: the regex stays an expression.
 */
export function parseRegex(regex) {
	if (typeof regex !== 'string' || !regex) return null;
	const src = regex;
	const n = src.length;
	let i = 0;
	let lead = '';
	let trail = '';
	let end = n;
	if (src[0] === '^') { lead = '^'; i = 1; }
	// A trailing `$` is the anchor unless a backslash escapes it (count them:
	// `\\$` is a backslash, then the anchor).
	if (end > i && src[end - 1] === '$') {
		let slashes = 0;
		for (let k = end - 2; k >= i && src[k] === '\\'; k--) slashes++;
		if (slashes % 2 === 0) { trail = '$'; end -= 1; }
	}

	// One literal character at i (escaped or not), or null.
	function literalAt(pos, inClass) {
		const c = src[pos];
		if (c === undefined || pos >= end) return null;
		if (c === '\\') {
			const next = src[pos + 1];
			if (next === undefined || pos + 1 >= end || !IDENTITY_ESCAPE.test(next)) return null;
			return { ch: next, len: 2 };
		}
		if (inClass) {
			if (c === ']' || c === '[') return null;
			return { ch: c, len: 1 };
		}
		if ('^$.|?*+()[]{}'.includes(c)) return null;
		return { ch: c, len: 1 };
	}

	function readQuantifier() {
		const c = src[i];
		let count = null;
		let len = 0;
		if (c === '*') { count = { type: 'any', n: null, m: null }; len = 1; }
		else if (c === '+') { count = { type: 'some', n: null, m: null }; len = 1; }
		else if (c === '?') { count = { type: 'range', n: 0, m: 1 }; len = 1; }
		else if (c === '{') {
			const m = /^\{([0-9]{1,3})(?:(,)([0-9]{0,3}))?\}/.exec(src.slice(i, end));
			if (!m) return { bad: true };
			len = m[0].length;
			const lo = Number(m[1]);
			if (m[2] === undefined) {
				if (lo < 1 || lo > MAX_COUNT) return { bad: true };
				count = { type: 'exactly', n: lo, m: null };
			} else if (m[3] === '') {
				if (lo === 0) count = { type: 'any', n: null, m: null };
				else if (lo === 1) count = { type: 'some', n: null, m: null };
				else return { bad: true };
			} else {
				const hi = Number(m[3]);
				if (hi < 1 || hi > MAX_COUNT || lo > hi) return { bad: true };
				count = { type: 'range', n: lo, m: hi };
			}
		} else {
			return { count: null, len: 0 };
		}
		// A lazy count (`+?`) or a second count: not a row.
		const after = src[i + len];
		if (i + len < end && after !== undefined && '*+?{'.includes(after)) return { bad: true };
		return { count, len };
	}

	function readClass() {
		// i is on '['.
		let j = i + 1;
		if (src[j] === '^') return null;
		const ranges = new Set();
		let chars = '';
		const startBody = j;
		while (j < end && src[j] !== ']') {
			if (src[j] === '\\' && src[j + 1] === 'd') { ranges.add('0-9'); j += 2; continue; }
			// A range: the three the kinds are made of, and no other.
			const three = src.slice(j, j + 3);
			if (src[j] !== '\\' && src[j + 1] === '-' && src[j + 2] !== undefined && src[j + 2] !== ']' && j + 2 < end) {
				if (!RANGES.includes(three)) return null;
				ranges.add(three);
				j += 3;
				continue;
			}
			const lit = literalAt(j, true);
			if (!lit) return null;
			// `-` stands for itself only first or last.
			if (lit.ch === '-' && lit.len === 1 && j !== startBody && src[j + 1] !== ']') return null;
			if (isSurrogate(lit.ch)) return null;
			chars += lit.ch;
			j += lit.len;
		}
		if (j >= end || j === startBody) return null;
		const body = { len: j + 1 - i };
		if (ranges.size && chars) return null;
		if (ranges.size) {
			const key = [...ranges].sort().join('|');
			const kind = RANGE_KINDS[key];
			return kind ? { ...body, kind } : null;
		}
		return { ...body, kind: 'chars', text: chars };
	}

	function readGroup() {
		// i is on '('. `(?:text)` or `(text)`, literal characters only.
		let j = i + 1;
		if (src[j] === '?') {
			if (src[j + 1] !== ':') return null;
			j += 2;
		}
		let text = '';
		while (j < end && src[j] !== ')') {
			const lit = literalAt(j, false);
			if (!lit) return null;
			text += lit.ch;
			j += lit.len;
		}
		if (j >= end || !text) return null;
		return { len: j + 1 - i, text };
	}

	const rows = [];
	let textRun = null; // { text, src } of uncounted literal characters
	let spaceRun = null; // { n, src } of uncounted spaces
	const flushText = () => {
		if (textRun) rows.push({ id: newId(), kind: 'text', text: textRun.text, count: { type: 'exactly', n: 1, m: null }, src: textRun.src });
		textRun = null;
	};
	const flushSpaces = () => {
		if (spaceRun) rows.push({ id: newId(), kind: 'space', text: '', count: { type: 'exactly', n: spaceRun.n, m: null }, src: spaceRun.src });
		spaceRun = null;
	};
	const flush = () => { flushText(); flushSpaces(); };

	while (i < end) {
		const c = src[i];
		const start = i;
		let atom = null; // { kind, text? } for a row of its own
		let literal = null;
		if (c === '[') {
			const cls = readClass();
			if (!cls) return null;
			i += cls.len;
			atom = { kind: cls.kind, text: cls.text || '' };
		} else if (c === '(') {
			const group = readGroup();
			if (!group) return null;
			i += group.len;
			atom = { kind: 'text', text: group.text };
		} else if (c === '.') {
			i += 1;
			atom = { kind: 'anything', text: '' };
		} else if (c === '\\' && src[i + 1] === 'd') {
			i += 2;
			atom = { kind: 'digits', text: '' };
		} else {
			const lit = literalAt(i, false);
			if (!lit) return null;
			i += lit.len;
			literal = lit.ch;
		}
		const q = readQuantifier();
		if (q.bad) return null;
		i += q.len;
		const rowSrc = src.slice(start, i);

		if (atom) {
			flush();
			rows.push({ id: newId(), kind: atom.kind, text: atom.text, count: q.count || { type: 'exactly', n: 1, m: null }, src: rowSrc });
			continue;
		}
		// A counted half of a surrogate pair counts half a character.
		if (q.count && isSurrogate(literal)) return null;
		if (literal === ' ') {
			flushText();
			if (q.count) {
				flushSpaces();
				rows.push({ id: newId(), kind: 'space', text: '', count: q.count, src: rowSrc });
			} else {
				spaceRun = spaceRun ? { n: spaceRun.n + 1, src: spaceRun.src + rowSrc } : { n: 1, src: rowSrc };
			}
			continue;
		}
		flushSpaces();
		if (q.count) {
			flushText();
			rows.push({ id: newId(), kind: 'text', text: literal, count: q.count, src: rowSrc });
		} else {
			textRun = textRun ? { text: textRun.text + literal, src: textRun.src + rowSrc } : { text: literal, src: rowSrc };
		}
	}
	flush();
	if (!rows.length) return null;
	return { rows, lead, trail };
}

// ------------------------------------------------------------ the problems

const isInt = v => Number.isInteger(v);

/** Why one row cannot be written yet, as a sentence, or null. */
export function rowProblem(row) {
	const kind = kindOf(row?.kind);
	if (!kind) return 'Pick what this row takes.';
	if (row.kind === 'text' && !row.text) return 'Type the text.';
	if (row.kind === 'chars') {
		if (!row.text) return 'Type the characters.';
		if ([...row.text].some(c => c.length > 1)) return 'An emoji can’t be one of these characters. Use “The text…” for it.';
	}
	const count = row.count || {};
	if (count.type === 'exactly') {
		if (!isInt(count.n) || count.n < 1 || count.n > MAX_COUNT) return `Enter a count from 1 to ${MAX_COUNT}.`;
	} else if (count.type === 'range') {
		if (!isInt(count.n) || !isInt(count.m) || count.n < 0 || count.m < 1 || count.n > MAX_COUNT || count.m > MAX_COUNT) {
			return `Enter counts from 0 to ${MAX_COUNT}.`;
		}
		if (count.n > count.m) return 'The first count must not be larger than the second.';
	} else if (count.type !== 'some' && count.type !== 'any') {
		return 'Pick how many.';
	}
	return null;
}

const lowest = count => (count.type === 'exactly' ? count.n : (count.type === 'range' ? count.n : (count.type === 'some' ? 1 : 0)));
// How many different lengths a row can take, at most (a value is at most 255
// characters, so an open count has 256).
const width = count => (count.type === 'exactly' ? 1 : (count.type === 'range' ? count.m - count.n + 1 : MAX_COUNT + 1));

// The characters a row can take, as categories and literal characters, with
// letters folded when the pattern ignores case.
function charsOf(row, flags) {
	const out = { cats: new Set(), chars: new Set() };
	const fold = flags === 'i';
	const addChar = c => {
		if (/[A-Z]/.test(c)) { out.cats.add('u'); if (fold) out.cats.add('l'); return; }
		if (/[a-z]/.test(c)) { out.cats.add('l'); if (fold) out.cats.add('u'); return; }
		if (/[0-9]/.test(c)) { out.cats.add('d'); return; }
		if (c === ' ') { out.cats.add('s'); return; }
		out.chars.add(c);
		if (fold) { out.chars.add(c.toLowerCase()); out.chars.add(c.toUpperCase()); }
	};
	if (row.kind === 'text' || row.kind === 'chars') [...String(row.text || '')].forEach(addChar);
	else {
		const cats = kindOf(row.kind)?.cats || '';
		for (const c of cats) out.cats.add(c);
		if (fold && (out.cats.has('u') || out.cats.has('l'))) { out.cats.add('u'); out.cats.add('l'); }
	}
	return out;
}

function overlap(a, b) {
	if (a.cats.has('*') || b.cats.has('*')) return true;
	for (const c of a.cats) if (b.cats.has(c)) return true;
	for (const c of a.chars) if (b.chars.has(c)) return true;
	return false;
}

/**
 * The rows split into CHAINS: runs of neighbours that can take the same
 * characters, where the engine may have to try every way of sharing a
 * stretch of the value between them. Two neighbours that take no character in
 * common, and both take at least one, always split the value at one place; a
 * row that may take nothing joins its neighbours to each other.
 */
export function chainsOf(rows, flags) {
	const sets = rows.map(row => charsOf(row, flags));
	const chains = [];
	let current = [];
	rows.forEach((row, index) => {
		if (current.length) {
			const prev = rows[index - 1];
			const joined = overlap(sets[index - 1], sets[index]) || lowest(prev.count) === 0 || lowest(row.count) === 0;
			if (!joined) { chains.push(current); current = []; }
		}
		current.push(index);
	});
	if (current.length) chains.push(current);
	return chains;
}

/**
 * chainCost(rows, flags) -> { cost, worst }
 *
 * An upper bound on the ways a value can be shared out between the rows: in
 * each chain, the product of its varying rows' widths without the widest (the
 * last varying row's length is then fixed); across chains these multiply; and
 * one chain is tried to the end, which adds one full width. `worst` is the
 * chain with the most freedom, for the sentence that names it.
 */
export function chainCost(rows, flags) {
	let cost = MAX_COUNT + 1;
	let worst = null;
	let worstFreedom = 1;
	for (const chain of chainsOf(rows, flags)) {
		const widths = chain.map(index => width(rows[index].count)).filter(w => w > 1);
		if (widths.length < 2) continue;
		const freedom = widths.reduce((a, b) => a * b, 1) / Math.max(...widths);
		cost *= freedom;
		if (freedom > worstFreedom) { worstFreedom = freedom; worst = chain; }
	}
	return { cost, worst };
}

/**
 * piecesProblem(pieces, flags) -> null | { reason, text, row? }
 *
 * Why the rows cannot be written as a pattern yet: no rows; a row not filled
 * in (`row` is its index); the vendored rule's limits on the regex they write
 * (200 characters, 25 counts); or the cost rule. Rows with a problem write
 * nothing, so they never store a pattern the API would refuse.
 */
export function piecesProblem(pieces, flags) {
	const rows = pieces?.rows || [];
	if (!rows.length) return { reason: 'empty', text: 'Add a row, or type examples below and keepr proposes the rows.' };
	for (let index = 0; index < rows.length; index++) {
		const text = rowProblem(rows[index]);
		if (text) return { reason: 'row', row: index, text };
	}
	if (rows.length > MAX_ROWS) return { reason: 'rows', text: `A pattern can have at most ${MAX_ROWS} rows.` };
	const { cost, worst } = chainCost(rows, flags);
	if (cost > MAX_COST) {
		const first = worst[0] + 1;
		const last = worst[worst.length - 1] + 1;
		return {
			reason: 'cost',
			text: `Rows ${first} to ${last} can each take a varying number of the same characters, so checking a value would be slow. Give one of them an exact count, or put a different character between them.`,
		};
	}
	const regex = rowsToRegex(pieces);
	const checked = checkPattern({ regex, flags: flags || undefined, message: 'm' });
	if (!checked.ok) {
		if (checked.reason === 'too_long') return { reason: 'too_long', text: `These rows write a pattern longer than keepr allows (${MAX_REGEX} characters). Remove a row or shorten a text.` };
		if (checked.reason === 'unsafe') return { reason: 'unsafe', text: `A pattern can have at most ${MAX_REPETITIONS} rows with a count other than exactly 1.` };
		return { reason: checked.reason, text: 'keepr can’t write these rows as a pattern. Change a row.' };
	}
	return null;
}

/**
 * fitsPieces(regex, flags) -> boolean
 *
 * p02 (the owner, 2026-10-18): a stored custom regex reopens as rows when the
 * rows express it exactly — they read it, write it back byte for byte, and
 * have nothing to complain about. Anything else opens as the expression.
 */
export function fitsPieces(regex, flags) {
	const pieces = parseRegex(regex);
	if (!pieces) return false;
	if (rowsToRegex(pieces) !== regex) return false;
	return !piecesProblem(pieces, flags);
}

// --------------------------------------------------------------- examples

const category = c => {
	if (/[A-Z]/.test(c)) return 'u';
	if (/[a-z]/.test(c)) return 'l';
	if (/[0-9]/.test(c)) return 'd';
	if (c === ' ') return 's';
	return 'o';
};

// An example as runs: capitals and small letters (or, `byCase` false, letters
// of either case), digits, spaces, and runs of any other character, which are
// kept as their text.
function runsOf(value, byCase) {
	const runs = [];
	for (const ch of value) {
		const raw = category(ch);
		const cat = !byCase && (raw === 'u' || raw === 'l') ? 'a' : raw;
		const last = runs[runs.length - 1];
		if (last && last.cat === cat) { last.text += ch; last.length += 1; }
		else runs.push({ cat, text: ch, length: 1 });
	}
	return runs;
}

const signatureOf = runs => runs.map(run => (run.cat === 'o' ? `o:${run.text}` : run.cat)).join('\u0001');

function letterKind(texts, digits) {
	const all = texts.join('');
	const hasUpper = /[A-Z]/.test(all);
	const hasLower = /[a-z]/.test(all);
	if (digits) {
		if (hasUpper && !hasLower) return 'capitals-digits';
		if (hasLower && !hasUpper) return 'small-digits';
		return 'alnum';
	}
	if (hasUpper && !hasLower) return 'capitals';
	if (hasLower && !hasUpper) return 'small';
	return 'letters';
}

function countFor(lengths) {
	const lo = Math.min(...lengths);
	const hi = Math.max(...lengths);
	return lo === hi ? { type: 'exactly', n: lo, m: null } : { type: 'range', n: lo, m: Math.min(hi, MAX_COUNT) };
}

/**
 * proposeFromExamples(examples) -> { rows, note } | null
 *
 * p01 (the owner, 2026-10-18): examples propose, rows refine. `examples` are
 * values that should fit, as they will be STORED (the element's trim and case
 * already applied). When every example has the same shape — the same runs of
 * letters, digits, spaces and other characters — each run becomes a row: the
 * count every example shares is asked for exactly (4 digits is exactly 4, not
 * 4 or more), different counts as the range between them, and a run that is
 * the same text in every example (two or more) is kept as that text. When the
 * shapes differ, one row of the narrowest kind that takes every character,
 * from the shortest to the longest. `note` is the one line saying how keepr
 * read them.
 */
export function proposeFromExamples(examples) {
	const values = (examples || []).map(v => String(v ?? '')).filter(v => v.length);
	if (!values.length) return null;
	// Read by case first ("Ana" is a capital, then small letters); when the
	// examples' cases do not line up, as letters of either case.
	const shaped = byCase => {
		const runs = values.map(v => runsOf(v, byCase));
		return runs.every(r => signatureOf(r) === signatureOf(runs[0])) ? runs : null;
	};
	const all = shaped(true) || shaped(false);
	const same = Boolean(all);
	const several = values.length > 1;

	if (!same) {
		const chars = values.join('');
		const onlyLettersDigits = /^[A-Za-z0-9]+$/.test(chars);
		const hasLetters = /[A-Za-z]/.test(chars);
		const hasDigits = /[0-9]/.test(chars);
		let kind = 'anything';
		if (onlyLettersDigits) kind = hasLetters ? letterKind([chars], hasDigits) : 'digits';
		const count = countFor(values.map(v => [...v].length));
		return {
			rows: [newRow({ kind, count })],
			note: 'Your examples don’t share one shape, so keepr proposed one row that fits them all. Add rows to narrow it.',
		};
	}

	let exact = false;
	let ranged = false;
	let kept = false;
	const rows = [];
	all[0].forEach((run, index) => {
		const column = all.map(runs => runs[index]);
		const texts = column.map(r => r.text);
		const lengths = column.map(r => r.length);
		const count = countFor(lengths);
		const sameText = texts.every(t => t === texts[0]);
		if (run.cat === 'o' || (run.cat !== 's' && several && sameText)) {
			if (run.cat !== 'o') kept = true;
			// Literal text next to literal text is one row.
			const prev = rows[rows.length - 1];
			if (prev && prev.kind === 'text' && prev.count.type === 'exactly' && prev.count.n === 1) prev.text += texts[0];
			else rows.push(newRow({ kind: 'text', text: texts[0], count: { type: 'exactly', n: 1, m: null } }));
			return;
		}
		if (count.type === 'exactly') { if (count.n > 1) exact = true; } else ranged = true;
		const kind = { d: 'digits', s: 'space', u: 'capitals', l: 'small' }[run.cat] || letterKind(texts, false);
		rows.push(newRow({ kind, count }));
	});

	const parts = [];
	if (exact) parts.push('a count every example shares is asked for exactly');
	if (ranged) parts.push('different counts as the range between them');
	if (kept) parts.push('text every example shares is kept as that text');
	const note = parts.length
		? `keepr read your examples exactly: ${parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0]}. Change a row to allow more.`
		: null;
	return { rows, note };
}

// --------------------------------------------------------------- in words

const quoted = text => `“${text}”`;

function countWords(count, one, many) {
	switch (count.type) {
		case 'exactly': return count.n === 1 ? `one ${one}` : `${count.n} ${many}`;
		case 'range': return count.n === 0 && count.m === 1 ? `an optional ${one}` : `${count.n} to ${count.m} ${many}`;
		case 'some': return `one or more ${many}`;
		case 'any': return `any number of ${many}`;
		default: return many;
	}
}

function timesWords(count) {
	switch (count.type) {
		case 'exactly': return count.n === 1 ? '' : ` ${count.n} times`;
		case 'range': return count.n === 0 && count.m === 1 ? ', optional' : ` ${count.n} to ${count.m} times`;
		case 'some': return ' one or more times';
		case 'any': return ' any number of times';
		default: return '';
	}
}

/** One row in words: "3 capital letters", "the text “INV-”", "one of “-._”". */
export function describeRow(row) {
	if (rowProblem(row)) return null;
	if (row.kind === 'text') return `the text ${quoted(row.text)}${timesWords(row.count)}`;
	if (row.kind === 'chars') return countWords(row.count, `character from ${quoted(row.text)}`, `characters from ${quoted(row.text)}`);
	const kind = kindOf(row.kind);
	return countWords(row.count, kind.noun, kind.nouns);
}

/** The rows as one sentence, or null while one is not filled in. */
export function describeRows(rows) {
	const words = (rows || []).map(describeRow);
	if (!words.length || words.some(w => !w)) return null;
	return words.join(', then ');
}
